import type { MonthlyRequestTrend, SceneTrend } from '../../types/styling-analysis.js';

/** @graph-connects none */
interface RequestTrendsPanelProps {
  sceneTrends: SceneTrend[];
  monthlyTrends: MonthlyRequestTrend[];
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Display request trend analysis: scene distribution bars and monthly trend table.
 * @graph-connects none
 */
export function RequestTrendsPanel({ sceneTrends, monthlyTrends }: RequestTrendsPanelProps) {
  const maxSceneCount = Math.max(1, ...sceneTrends.map((s) => s.count));
  const totalSceneCount = sceneTrends.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
          着用シーン別リクエスト分布
        </h3>
        {sceneTrends.length === 0 ? (
          <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            データがありません
          </div>
        ) : (
          <div className="space-y-3">
            {sceneTrends.map((scene) => (
              <div key={scene.sceneId} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-sm text-[var(--color-text-secondary)]">
                  {scene.sceneTitle}
                </span>
                <div className="flex-1">
                  <div className="h-7 overflow-hidden rounded-md bg-[var(--color-bg-elevated)]">
                    <div
                      className="flex h-full items-center rounded-md bg-[var(--color-accent-primary)] px-2 text-xs font-medium text-white transition-all"
                      style={{
                        width: `${String(Math.max(3, (scene.count / maxSceneCount) * 100))}%`,
                      }}
                    >
                      {String(scene.count)}件
                    </div>
                  </div>
                </div>
                <span className="w-12 shrink-0 text-right text-xs tabular-nums text-[var(--color-text-muted)]">
                  {totalSceneCount > 0
                    ? `${String(Math.round((scene.count / totalSceneCount) * 100))}%`
                    : '-'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-4 text-sm font-semibold text-[var(--color-text-primary)]">
          月別リクエスト推移
        </h3>
        {monthlyTrends.length === 0 ? (
          <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            データがありません
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                    月
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                    総オーダー数
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                    リクエスト文あり
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                    シーン指定あり
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                    再選定あり
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-[var(--color-text-secondary)]">
                    リクエスト率
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyTrends.map((m) => (
                  <tr
                    key={m.month}
                    className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)]"
                  >
                    <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-primary)]">
                      {m.month}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{String(m.totalOrders)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{String(m.withRequest)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{String(m.withScene)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {String(m.withRejectReason)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[var(--color-accent-primary)]">
                      {m.totalOrders > 0
                        ? `${String(Math.round((m.withRequest / m.totalOrders) * 100))}%`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
